import React, { useState, useEffect, useCallback } from 'react';
import {
  Flame,
  AlertTriangle,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Zap,
  Server,
  Database,
  HardDrive,
  Network,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import useSimulationStore from '../../store/simulationStore';

const SCENARIO_ICONS = {
  database_failure: Database,
  cache_failure: HardDrive,
  load_balancer_failure: Server,
  single_server_failure: Server,
  network_partition: Network,
  degraded_database: Database,
  cdn_failure: HardDrive,
  queue_failure: Server,
};

const SEVERITY_COLORS = {
  minor: 'text-yellow-400 bg-yellow-900/30 border-yellow-700',
  major: 'text-orange-400 bg-orange-900/30 border-orange-700',
  catastrophic: 'text-red-400 bg-red-900/30 border-red-700',
};

const ChaosPanel = ({ designJson, componentList, nodes = [] }) => {
  const {
    isSimulating,
    chaosResults,
    spofAnalysis,
    scenarios,
    error,
    fetchScenarios,
    runScenario,
    testAllSpofs,
    testNodeSpof,
    clearResults,
  } = useSimulationStore();

  const [selectedScenario, setSelectedScenario] = useState(null);
  const [expandedResults, setExpandedResults] = useState({});
  const [clickToFailMode, setClickToFailMode] = useState(false);

  useEffect(() => {
    fetchScenarios();
  }, [fetchScenarios]);

  const handleRunScenario = useCallback(async () => {
    if (!selectedScenario) return;
    try {
      await runScenario(designJson, componentList, selectedScenario);
    } catch (err) {
      console.error('Chaos simulation failed:', err);
    }
  }, [designJson, componentList, selectedScenario, runScenario]);

  const handleTestAllSpofs = useCallback(async () => {
    try {
      await testAllSpofs(designJson, componentList);
    } catch (err) {
      console.error('SPOF analysis failed:', err);
    }
  }, [designJson, componentList, testAllSpofs]);

  const handleTestNodeSpof = useCallback(async (nodeId) => {
    try {
      const result = await testNodeSpof(designJson, componentList, nodeId);
      return result;
    } catch (err) {
      console.error('Node SPOF test failed:', err);
    }
  }, [designJson, componentList, testNodeSpof]);

  const toggleResultExpanded = (idx) => {
    setExpandedResults((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  const criticalSpofs = spofAnalysis?.filter((s) => s.is_spof && s.failure_impact === 'complete') || [];
  const partialSpofs = spofAnalysis?.filter((s) => s.is_spof && s.failure_impact === 'partial') || [];

  return (
    <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Flame className="w-5 h-5 text-orange-400" />
          <h3 className="font-semibold text-white">Chaos Engineering</h3>
        </div>
      </div>

      {/* Scenario Selector */}
      <div className="p-4 border-b border-slate-700 space-y-3">
        <label className="block text-sm text-slate-400">Failure Scenario</label>
        <select
          value={selectedScenario || ''}
          onChange={(e) => setSelectedScenario(e.target.value || null)}
          className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
        >
          <option value="">Select a scenario...</option>
          {scenarios.map((scenario) => (
            <option key={scenario.id} value={scenario.id}>
              {scenario.name}
            </option>
          ))}
        </select>

        {selectedScenario && scenarios.find((s) => s.id === selectedScenario) && (
          <div className="text-sm text-slate-400">
            {scenarios.find((s) => s.id === selectedScenario).description}
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={handleRunScenario}
            disabled={!selectedScenario || isSimulating}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-slate-600 text-white rounded-lg transition-colors"
          >
            {isSimulating ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4" />
                Run Scenario
              </>
            )}
          </button>
        </div>
      </div>

      {/* SPOF Analysis Section */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-slate-300 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-red-400" />
            Single Point of Failure Analysis
          </h4>
          <button
            onClick={handleTestAllSpofs}
            disabled={isSimulating}
            className="text-xs px-3 py-1 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center gap-1"
          >
            {isSimulating ? (
              <RefreshCw className="w-3 h-3 animate-spin" />
            ) : (
              <Shield className="w-3 h-3" />
            )}
            Test All
          </button>
        </div>

        {spofAnalysis && spofAnalysis.length > 0 ? (
          <div className="space-y-2">
            {/* Critical SPOFs */}
            {criticalSpofs.length > 0 && (
              <div className="bg-red-900/20 border border-red-700 rounded-lg p-3">
                <div className="flex items-center gap-2 text-red-400 font-medium mb-2">
                  <XCircle className="w-4 h-4" />
                  {criticalSpofs.length} Critical SPOF{criticalSpofs.length > 1 ? 's' : ''}
                </div>
                <div className="space-y-1">
                  {criticalSpofs.map((spof, idx) => (
                    <div key={idx} className="text-sm">
                      <span className="text-white">{spof.node_label}</span>
                      <span className="text-slate-400 ml-2">- {spof.mitigation_advice}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Partial SPOFs */}
            {partialSpofs.length > 0 && (
              <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-3">
                <div className="flex items-center gap-2 text-yellow-400 font-medium mb-2">
                  <AlertTriangle className="w-4 h-4" />
                  {partialSpofs.length} Partial SPOF{partialSpofs.length > 1 ? 's' : ''}
                </div>
                <div className="space-y-1">
                  {partialSpofs.map((spof, idx) => (
                    <div key={idx} className="text-sm">
                      <span className="text-white">{spof.node_label}</span>
                      <span className="text-slate-400 ml-2">
                        ({Math.round(spof.traffic_affected_pct || 0)}% traffic affected)
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No SPOFs */}
            {criticalSpofs.length === 0 && partialSpofs.length === 0 && (
              <div className="bg-green-900/20 border border-green-700 rounded-lg p-3">
                <div className="flex items-center gap-2 text-green-400 font-medium">
                  <ShieldCheck className="w-4 h-4" />
                  No single points of failure detected
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-slate-500">
            Run SPOF analysis to identify single points of failure in your design.
          </p>
        )}
      </div>

      {/* Chaos Results */}
      {chaosResults && chaosResults.length > 0 && (
        <div className="p-4">
          <h4 className="text-sm font-medium text-slate-300 mb-3">Chaos Test Results</h4>
          <div className="space-y-2">
            {chaosResults.map((result, idx) => {
              const Icon = SCENARIO_ICONS[result.scenario?.id] || Server;
              const isExpanded = expandedResults[idx];

              return (
                <div
                  key={idx}
                  className="bg-slate-700/50 rounded-lg border border-slate-600 overflow-hidden"
                >
                  <button
                    onClick={() => toggleResultExpanded(idx)}
                    className="w-full p-3 flex items-center justify-between hover:bg-slate-700/70 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-4 h-4 text-slate-400" />
                      <span className="text-white font-medium">
                        {result.scenario?.name || 'Unknown Scenario'}
                      </span>
                      {result.system_survived ? (
                        <span className="text-xs px-2 py-0.5 bg-green-900/30 text-green-400 rounded">
                          Survived
                        </span>
                      ) : result.total_outage ? (
                        <span className="text-xs px-2 py-0.5 bg-red-900/30 text-red-400 rounded">
                          Total Outage
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 bg-yellow-900/30 text-yellow-400 rounded">
                          Partial Outage
                        </span>
                      )}
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                  </button>

                  {isExpanded && (
                    <div className="p-3 border-t border-slate-600 space-y-3">
                      {/* Impact Stats */}
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-slate-400">Traffic Dropped:</span>
                          <span className={`ml-2 ${(result.dropped_traffic_pct || 0) > 50 ? 'text-red-400' : 'text-yellow-400'}`}>
                            {result.dropped_traffic_pct?.toFixed(1) || 0}%
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400">Latency Increase:</span>
                          <span className={`ml-2 ${(result.latency_increase_pct || 0) > 100 ? 'text-red-400' : 'text-yellow-400'}`}>
                            {result.latency_increase_pct?.toFixed(1) || 0}%
                          </span>
                        </div>
                      </div>

                      {/* Resilience Score */}
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400 text-sm">Resilience Score:</span>
                        <div className="flex-1 bg-slate-600 rounded-full h-2">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${result.resilience_score || 0}%`,
                              backgroundColor:
                                (result.resilience_score || 0) >= 70
                                  ? '#22c55e'
                                  : (result.resilience_score || 0) >= 40
                                  ? '#eab308'
                                  : '#ef4444',
                            }}
                          />
                        </div>
                        <span className="text-white text-sm font-medium">
                          {Math.round(result.resilience_score || 0)}
                        </span>
                      </div>

                      {/* Recommendations */}
                      {result.recommendations?.length > 0 && (
                        <div>
                          <span className="text-slate-400 text-sm">Recommendations:</span>
                          <ul className="mt-1 space-y-1">
                            {result.recommendations.map((rec, ridx) => (
                              <li key={ridx} className="text-sm text-slate-300 flex items-start gap-2">
                                <span className="text-cyan-400 mt-0.5">•</span>
                                {rec}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Redundancy Used */}
                      {result.redundancy_used && (
                        <div className="flex items-center gap-2 text-sm text-green-400">
                          <CheckCircle className="w-4 h-4" />
                          Failover mechanisms activated successfully
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="p-4">
          <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChaosPanel;
